import styled from "styled-components";
import { useRecoilValue } from "recoil";

import CheckBox from "components/atoms/input/CheckBox";
import COLORS from "components/atoms/colors";
import SquareMeasure from "components/layouts/Header/ModalPalette/Palette/SquareMeasure";
import Image from "components/organisms/Image";

import Scene from "types/Scene";
import PinIdInfo from "types/PinIdInfo";
import Project from "types/Project";

import authAtom from "stores/auth";
import useSort from "hooks/useSort";

import { ReactComponent as IconArrow } from "assets/images/common/arrow-down.svg";

interface Props {
  project: Project;
  scene: Scene;
  sceneIndex: number;
  pinIdInfo: PinIdInfo[];
  onChangeEachPin: (params: { checked: boolean; pinId: number }) => void;
  onChangeScenePins: (params: {
    checked: boolean;
    idInfos: PinIdInfo[];
  }) => void;
  onChageEachMeasure: (params: {
    pinId: number;
    squareMeasure: string;
  }) => void;
  isNoMeasure: boolean;
}

const cols = [
  "30px",
  "60px",
  "*",
  "80px",
  "*",
  "*",
  "*",
  "*",
  "60px",
  "*",
  "80px",
];

// 팔레트 - 리스트형
const PaletteListItem = ({
  scene,
  project,
  sceneIndex,
  pinIdInfo,
  onChangeEachPin,
  onChangeScenePins,
  onChageEachMeasure,
  isNoMeasure,
}: Props) => {
  const auth = useRecoilValue(authAtom);

  // 핀 목록 정렬 커스텀 훅 (0 : 오름차순, 1:내림차순, 2: 정렬기준없음)
  const { handleSort, sortList } = useSort({
    list: scene.pins,
  });

  // 씬 별 전체 선택 - 핀 목록
  const scenePinIds = scene.pins.map((pin) => ({
    pinId: pin.id,
    squareMeasure: "",
  }));

  return (
    <table className={`mb-10 scene${sceneIndex + 1}`}>
      <Caption>SCENE {sceneIndex + 1}</Caption>
      <colgroup>
        {cols.map((width, i) => (
          <col key={i} width={width} />
        ))}
      </colgroup>
      <THead>
        <tr>
          <Th>
            <div className="flex flex-col items-center">
              <CheckBox
                checked={
                  scene.pins.length > 0 &&
                  scene.pins.every(
                    (pin) => !!pinIdInfo.find((p) => p.pinId === pin.id)
                  )
                }
                onChange={(e) =>
                  onChangeScenePins({
                    checked: e.target.checked,
                    idInfos: scenePinIds,
                  })
                }
              />
            </div>
          </Th>
          <Th>이미지</Th>
          <Th>
            <button
              onClick={() =>
                handleSort({
                  sortKey: "productOption.product.name",
                })
              }
            >
              상품명
              <IconSort
                preserveAspectRatio="xMidYMid meet"
                viewBox="0 0 18 18"
              />
            </button>
          </Th>
          <Th>
            <button
              onClick={() =>
                handleSort({
                  sortKey: "productOption.product.category.name",
                })
              }
            >
              구분
              <IconSort
                preserveAspectRatio="xMidYMid meet"
                viewBox="0 0 18 18"
              />
            </button>
          </Th>
          <Th>종류</Th>
          <Th>사이즈(W*L*T)</Th>
          <Th>
            <button
              onClick={() =>
                handleSort({
                  sortKey: "productOption.product.manufacturer",
                })
              }
            >
              제조사
              <IconSort
                preserveAspectRatio="xMidYMid meet"
                viewBox="0 0 18 18"
              />
            </button>
          </Th>
          <Th>
            <button
              onClick={() =>
                handleSort({
                  sortKey: "productOption.product.origin",
                })
              }
            >
              원산지
              <IconSort
                preserveAspectRatio="xMidYMid meet"
                viewBox="0 0 18 18"
              />
            </button>
          </Th>

          <Th>
            <button
              onClick={() =>
                handleSort({
                  sortKey: "productOption.price",
                })
              }
            >
              단가
              <IconSort
                preserveAspectRatio="xMidYMid meet"
                viewBox="0 0 18 18"
              />
            </button>
          </Th>
          <Th>프로젝트 - 위치</Th>
          <Th>면적(m2)</Th>
        </tr>
      </THead>
      <TBody>
        {sortList.map((pin, i) => {
          const {
            image,
            name,
            category,
            subCategory,
            manufacturer,
            location,
            country,
            origin,
          } = pin.productOption.product;

          const { price, size } = pin.productOption;

          const noMeasureItems = pinIdInfo.filter((p) => !p.squareMeasure);

          const emptyMeasure =
            isNoMeasure && !!noMeasureItems.find((p) => p.pinId === pin.id);

          return (
            <tr key={i}>
              <Td>
                <div className="flex flex-col items-center">
                  <CheckBox
                    checked={!!pinIdInfo.find((p) => p.pinId === pin.id)}
                    onChange={(e) =>
                      onChangeEachPin({
                        checked: e.target.checked,
                        pinId: pin.id,
                      })
                    }
                  />
                </div>
              </Td>
              <Td>
                <ImgWrap>
                  <Image src={image} />
                </ImgWrap>
              </Td>
              <Td>{name}</Td>
              <Td>{category?.name}</Td>
              <Td>{subCategory}</Td>
              <Td>{size}</Td>
              <Td>{manufacturer}</Td>
              <Td>{origin === "수입산" ? country : origin}</Td>

              <Td>
                {auth.type === "user"
                  ? "별도문의"
                  : `￦${price?.toLocaleString()}`}
              </Td>
              <Td>
                {project.name} - {scene.title}
              </Td>
              <Td>
                <SquareMeasure
                  placeholder="적용면적입력"
                  className="text-center w-full !px-0"
                  onChageEachMeasure={(squareMeasure) =>
                    onChageEachMeasure({ pinId: pin.id, squareMeasure })
                  }
                  squareMeasure={
                    pinIdInfo.find((p) => p.pinId === pin.id)?.squareMeasure
                  }
                  focus={emptyMeasure}
                />
              </Td>
            </tr>
          );
        })}
      </TBody>
    </table>
  );
};

const Caption = styled.caption`
  margin-bottom: 14px;
  text-align: left;
  font-size: 20px;
  font-weight: 700;
`;

const THead = styled.thead`
  border-bottom: 1px solid ${COLORS.black["40%"]};
`;

const Th = styled.th`
  padding: 6px 10px;
  color: ${COLORS.black["60%"]};
  font-weight: 400;

  & > button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const TBody = styled.tbody`
  tr:first-child td {
    padding-top: 6px;
  }
`;

const Td = styled.td`
  text-align: center;
  padding: 2px 10px;
`;

const IconSort = styled(IconArrow)`
  width: 12px;
  height: 12px;

  path {
    fill: ${COLORS.black["60%"]};
  }
`;

const ImgWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  overflow: hidden;
  width: 40px;
  height: 40px;
  margin: 0 auto;
`;

export default PaletteListItem;
